import React, { useCallback, useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { shouldFetchData, status } from '../../../data/fetchStatus';
import { currentIsoDate, formatDate } from '../../../utils/date';
import Notation from '../../Shared/Notation';
import Services from '../../Services/services';
import useToast from '../../Toasts/useToast';
import {
	ToastHeaders,
	ToastThemes,
	ToastMessages,
} from '../../Toasts/ToastOptions/toastOptions';
import { ClientContext } from '../../Contexts/contexts';

function ExpertReview(props) {
	const clientContext = useContext(ClientContext);

	const enqueueToast = useToast();

	const [strengths, setStrengths] = useState([]);
	const [strengthsFetched, setStrengthsFetched] = useState(status.DEFAULT);

	const [reviewStrengths, setReviewStrengths] = useState([]);
	const [reviewStrengthsFetched, setReviewStrengthsFetched] = useState(
		status.DEFAULT
	);

	const [reviewExpert, setReviewExpert] = useState({});
	const [reviewExpertFetched, setReviewExpertFetched] = useState(
		status.DEFAULT
	);

	const [selectedStrengths, setSelectedStrengths] = useState([]);

	const fetchExpertReview = useCallback(async () => {
		if (
			clientContext.clientId === undefined ||
			reviewExpertFetched === status.FETCHED
		)
			return;

		setReviewExpertFetched(status.FETCHED);

		try {
			const data = await Services.clientReviewService.clientReviewExpert(
				clientContext.clientId
			);

			if (data.length !== 1) {
				setReviewExpert({
					clientId: clientContext?.clientExpertRelation?.clientId,
					expertId: clientContext?.clientExpertRelation?.expertId,
					rating: 1,
					comment: '',
					createdAt: null,
				});
				return;
			}

			setReviewExpert(data[0]);
		} catch (error) {
			enqueueToast(ToastMessages.ERROR_OCCURED_FETCH, {
				theme: ToastThemes.toastThemes.ERROR_OCCURED,
				header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
				duration: 3000,
			});
		}
	}, [clientContext, reviewExpertFetched, enqueueToast]);

	const fetchStrengths = useCallback(async () => {
		if (strengthsFetched === status.FETCHED) return;

		setStrengthsFetched(status.FETCHED);

		try {
			const strenghsData = await Services.clientReviewService.strengths();
			setStrengths(strenghsData);
		} catch (error) {
			enqueueToast(ToastMessages.ERROR_OCCURED_FETCH, {
				theme: ToastThemes.toastThemes.ERROR_OCCURED,
				header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
				duration: 3000,
			});
		}
	}, [strengthsFetched, enqueueToast]);

	const fetchReviewStrengths = useCallback(async () => {
		if (
			reviewExpert.id === undefined ||
			reviewStrengthsFetched === status.FETCHED
		)
			return;

		setReviewStrengthsFetched(status.FETCHED);

		try {
			const reviewStrengthsData =
				await Services.clientReviewService.reviewStrengthsByclientReviewExpertID(
					reviewExpert.id
				);

			setReviewStrengths(reviewStrengthsData);
		} catch (error) {
			enqueueToast(ToastMessages.ERROR_OCCURED_FETCH, {
				theme: ToastThemes.toastThemes.ERROR_OCCURED,
				header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
				duration: 3000,
			});
		}
	}, [reviewExpert.id, reviewStrengthsFetched, enqueueToast]);

	useEffect(() => {
		if (shouldFetchData(strengthsFetched)) fetchStrengths();
		if (shouldFetchData(reviewStrengthsFetched)) fetchReviewStrengths();
		if (shouldFetchData(reviewExpertFetched)) fetchExpertReview();
	}, [
		strengthsFetched,
		reviewStrengthsFetched,
		reviewExpertFetched,
		fetchStrengths,
		fetchReviewStrengths,
		fetchExpertReview,
	]);

	function updateReview(key, value) {
		reviewExpert[key] = value;
		setReviewExpert({ ...reviewExpert });
	}

	function UpdateSelectedStrengths(id) {
		let copy = [...selectedStrengths];
		if (copy.includes(id)) copy = copy.filter((strength) => strength !== id);
		else copy = [...copy, id];

		setSelectedStrengths(copy);
	}

	function validateReview() {
		if (reviewExpert.comment.length === 0) {
			return;
		}

		reviewExpert.createdAt = currentIsoDate;

		props.createExpertReview(reviewExpert, selectedStrengths, () => {
			setReviewExpertFetched(status.REFRESH);
			setReviewStrengthsFetched(status.REFRESH);
			clientContext?.refreshClientExpertReview();
		});
	}

	function IsChecked(strengthId) {
		if (reviewStrengths.length === 0) return false;

		return (
			reviewStrengths.filter((item) => item.strengthId === strengthId).length >
			0
		);
	}

	async function deleteExpertReview() {
		await props.deleteReview(reviewExpert.id, () => {
			setReviewExpertFetched(status.REFRESH);
			setSelectedStrengths([]);
			clientContext?.refreshClientPlatformReview();
		});
	}

	return (
		<div className="review-prestation-container">
			<div className="reviews-data">
				<div className="reviews-element-name">
					<span className="reviews-data-title">Nom expert</span>
					<span className="reviews-data">
						{`${clientContext?.clientExpertRelation?.expert?.firstName} ${clientContext?.clientExpertRelation?.expert?.lastName}`}
					</span>
				</div>
				<div className="reviews-element-name">
					<span className="reviews-data-title">Numéro dossier</span>
					<span className="reviews-data-elmnt">
						#{clientContext?.clientExpertRelation?.referenceNumber}
					</span>
				</div>
				<div className="reviews-element-name">
					<span className="reviews-data-title">Date prestation</span>
					<span className="reviews-data-elmnt">
						{props?.appointment?.appointmentDate !== undefined
							? formatDate(props?.appointment?.appointmentDate, 'DD.MM.YYYY')
							: 'N/A'}
					</span>
				</div>
				<div className="reviews-element-name">
					<Link to={'/reviews'} className="reviews-data-link">
						Notez cet expert
					</Link>
				</div>
				<div className="check-icon"></div>
			</div>
			<div className="reviews-forces-note">
				<div className="forces">
					<span className="title-forces">
						Sélectionnez les forces de cet expert
					</span>
					<div className="checkbox-forces">
						<div className="checkbox-columns">
							{strengths.map((strength, index) => (
								<div className="group-box-forces" key={index}>
									{reviewExpert.createdAt === null ? (
										<input
											type="checkbox"
											id={strength.id}
											name={strength.id}
											className="checkbox-custom"
											onChange={() => UpdateSelectedStrengths(strength.id)}
											defaultChecked={IsChecked(strength.id)}
										/>
									) : (
										<input
											type="checkbox"
											id={strength.id}
											name={strength.id}
											className="checkbox-custom"
											checked={IsChecked(strength.id)}
											disabled={true}
										/>
									)}
									<label htmlFor={strength.id} className="checkbox-label">
										{strength.name}
									</label>
								</div>
							))}
						</div>
					</div>
				</div>
				<div className="note-container">
					<span className="title-note">Donnez une note pour cet expert</span>
					<Notation
						rating={reviewExpert.rating}
						updateReview={(value) => updateReview('rating', value)}
						disabled={reviewExpert.createdAt !== null}
					/>
				</div>
			</div>
			<div className="reviews-comment">
				<label className="title-comment" htmlFor="comment-expert">
					Commentaire
				</label>
				<textarea
					id="comment-expert"
					name="comment-expert"
					required
					className="area-comment"
					placeholder="Indiquez vos commentaires"
					onChange={(e) => updateReview('comment', e.target.value)}
					value={reviewExpert.comment}
					disabled={reviewExpert.createdAt !== null}
				></textarea>
			</div>
			{reviewExpert.createdAt === null ? (
				<div className="validation-comment-container">
					<div className="validation-button" onClick={() => validateReview()}>
						Valider mon commentaire
					</div>
				</div>
			) : (
				<div className="validation-comment-container">
					<div
						className="validation-button delete-button"
						onClick={() => deleteExpertReview()}
					>
						Supprimer mon commentaire
					</div>
				</div>
			)}
		</div>
	);
}

export default ExpertReview;
